import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ModalService, ModalConfig } from '../../services/modal.service';

const CLOSE_DISTANCE_PX = 120;
const CLOSE_VELOCITY = 0.5;

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './modal.html',
  styleUrl: './modal.css',
})
export class ModalComponent implements OnInit, OnDestroy {
  @ViewChild('modalContent', { read: ViewContainerRef, static: true })
  contentContainer!: ViewContainerRef;

  config: ModalConfig | null = null;

  get isWide(): boolean {
    return !!this.config?.wide;
  }

  isDragging = false;
  dragTransform = '';

  private sub!: Subscription;
  private dragStartY = 0;
  private dragCurrentY = 0;
  private dragStartTime = 0;
  private dragPointerId: number | null = null;
  private isMobile = false;

  constructor(
    private modalService: ModalService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.updateIsMobile();

    this.sub = this.modalService.modalState$.subscribe((config) => {
      const wasOpen = !!this.config;
      const previousComponent = this.config?.component;
      this.config = config;
      const isOpen = !!config;

      this.resetDrag();

      if (this.contentContainer) {
        const componentChanged =
          previousComponent !== config?.component || (!wasOpen && isOpen);

        if (componentChanged) {
          this.contentContainer.clear();

          if (config) {
            const componentRef = this.contentContainer.createComponent(
              config.component,
            );

            if (config.inputs) {
              Object.entries(config.inputs).forEach(([key, value]) => {
                componentRef.setInput(key, value);
              });
            }
          }
        }
      }

      if (typeof document !== 'undefined') {
        if (isOpen) {
          document.body.style.overflow = 'hidden';
        } else if (wasOpen && !isOpen) {
          document.body.style.overflow = '';
        }
      }

      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }

    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateIsMobile();
  }

  @HostListener('window:keydown.escape')
  onEscape(): void {
    if (this.config) {
      this.close();
    }
  }

  close(): void {
    this.modalService.close();
  }

  onHeaderPointerDown(event: PointerEvent): void {
    if (!this.isMobile) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const target = event.target as HTMLElement;
    if (target.closest('.close-btn')) return;

    this.isDragging = true;
    this.dragPointerId = event.pointerId;
    this.dragStartY = event.clientY;
    this.dragCurrentY = 0;
    this.dragStartTime = performance.now();

    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.isDragging || event.pointerId !== this.dragPointerId) return;

    const delta = event.clientY - this.dragStartY;
    this.dragCurrentY = Math.max(0, delta);
    this.dragTransform =
      this.dragCurrentY > 0 ? `translateY(${this.dragCurrentY}px)` : '';
    this.cdr.detectChanges();
  }

  onPointerUp(event: PointerEvent): void {
    if (!this.isDragging || event.pointerId !== this.dragPointerId) return;

    const elapsed = Math.max(1, performance.now() - this.dragStartTime);
    const velocity = this.dragCurrentY / elapsed;

    const shouldClose =
      this.dragCurrentY > CLOSE_DISTANCE_PX || velocity > CLOSE_VELOCITY;

    this.releasePointerCapture(event);

    if (shouldClose) {
      this.close();
    } else {
      this.resetDrag();
      this.cdr.detectChanges();
    }
  }

  onPointerCancel(event: PointerEvent): void {
    if (!this.isDragging) return;

    this.releasePointerCapture(event);
    this.resetDrag();
    this.cdr.detectChanges();
  }

  private updateIsMobile(): void {
    this.isMobile = typeof window !== 'undefined' && window.innerWidth <= 700;
  }

  private releasePointerCapture(event: PointerEvent): void {
    const el = event.currentTarget as HTMLElement;

    if (this.dragPointerId != null && el.releasePointerCapture) {
      try {
        el.releasePointerCapture(this.dragPointerId);
      } catch {
        return;
      }
    }
  }

  private resetDrag(): void {
    this.isDragging = false;
    this.dragPointerId = null;
    this.dragCurrentY = 0;
    this.dragTransform = '';
  }
}
